/**
 * CUSTOM TABLE BODY ENTRY POINT
 *
 * This component renders a table body for any page that needs to render complex infinite scroll tables.
 * It is reused on pages like Companies Review Page, Companies Listing page, etc.
 *
 * @version 0.1
 */

import React from 'react';
import ReactDOM from 'react-dom';
import { isEqual, isEqualWith } from 'lodash';

import xhr from './../../utils/xhr';
import {GET, POST} from './../../constants';
import utils from './../../utils/utilities';
import api from './redux/CustomTableBody.api';

import { Rows, NoEntriesRow } from './templates/CustomTableBody.rows.jsx';

class CustomTableBody extends React.Component {

    /**
     * Sets the default values of the component parameters and does all required type validations.
     *
     * @constructor
     * @param  {Object} props   The properties passed to the component.
     */
    constructor(props) {
        super(props);

        // Data type checking
        if (!utils.isValidUrl(this.props.url)) {
            throw new TypeError('The URL specified for fetching the table data has an invalid format.');
        }

        this.checkRowsModel(this.props.rowsModel);

        // Component state initialization
        this.state = {
            emptyCellMessageId: this.props.emptyCellMessageId,
            entries:            [],
            noEntries:          false
        };

        this.dataStore = null;

        if (this.props.dataStore != null) {
            this.dataStore = this.props.dataStore;
        }

        this.tableIsMounted = false;
        this.scrollEventAdded = false;
        this.addingRows = false; // Flag for preventing more than one AJAX request for fetching table data to run at a
                                 // time
        this.isHeaderFixed = false;
        this.endReached = false;

        // Parse the URL parameters
        this.url = this.props.url;
        this.pageNumber = parseInt(this.props.startPageNumber, 10) ? parseInt(this.props.startPageNumber, 10) : 0;
        this.pageSize = parseInt(this.props.pageSize, 10) ? parseInt(this.props.pageSize, 10) : 50;
        this.verb = GET;
        if (this.props.verb) {
            this.verb = this.props.verb;
        }

        // The show/hide spinner functionality
        this.showSpinner = this.props.showSpinner;
        this.hideSpinner = this.props.hideSpinner;

        // Bind the current component context to methods that need it
        this.addRowsToTable = this.addRowsToTable.bind(this);
        this.addInfiniteScroll = this.addInfiniteScroll.bind(this);
        this.toggleFixedHeader = this.toggleFixedHeader.bind(this);
        this.setEntries = this.setEntries.bind(this);
        this.getEntries = this.getEntries.bind(this);

    }

    /**
     * Adds the first rows to the table if there are any.
     */
    componentWillMount() {

        this.addRowsToTable(
            this.url,
            this.pageNumber,
            this.pageSize,
            this.props.urlParams,
            this.showSpinner,
            this.hideSpinner,
            this.dataStore,
            this.props.tableRequestPayload
        );

    }

    /**
     * Refresh the table when the url parameters change.
     *
     * @param  {Object} nextProps   The newly updated component properties.
     */
    componentWillReceiveProps(nextProps) {

        if (!isEqual(this.props.urlParams, nextProps.urlParams) ||
            !isEqual(this.props.tableRequestPayload, nextProps.tableRequestPayload) ||
            this.props.url !== nextProps.url) {

            if (!this.props.preventScrollToTop) {
                window.scrollTo(0, 0);
            }

            let clearEntries = true;
            this.pageNumber = 0;
            this.endReached = false;
            this.url = nextProps.url;

            this.dataStore = nextProps.dataStore;

            /* When the URL changes the custom table body is removed and added
             * again in the DOM, thus its event listeners need to be removed.
             */
            if (this.props.innerTable && this.rootDOMNode) {
                this.rootDOMNode.removeEventListener('scroll', this.scrollHandler);
            }

            this.addRowsToTable(
                this.url,
                this.pageNumber,
                this.pageSize,
                nextProps.urlParams,
                this.showSpinner,
                this.hideSpinner,
                this.dataStore,
                nextProps.tableRequestPayload,
                clearEntries
            ).then((response) => {

                /* When the URL changes the custom table body is removed and added
                 * again in the DOM, thus its event listeners need to be re-added.
                 */
                if (this.props.innerTable) {
                    this.addInfiniteScroll();
                }

                // Execute the "entriesSortFinished" lifecycle event handler if any
                // if (utils.isObject(this.props.lifecycleEvents) &&
                //     typeof this.props.lifecycleEvents.entriesSortFinished === 'function') {
                //     this.props.lifecycleEvents.entriesSortFinished();
                // }

            });

        }

    }

    /**
     * Adds the infinite scrolling facility.
     */
    componentDidMount() {

        this.tableIsMounted = true;

    }

    componentDidUpdate(prevProps, prevState) {

        this.rootDOMNode = ReactDOM.findDOMNode(this);

        if (this.state.entries.length > 0 && !this.scrollEventAdded) {
            this.addInfiniteScroll();
            this.scrollEventAdded = true;
        }

        if (this.props.innerTable) {
            let tableHeaderHeight = this.rootDOMNode.parentNode.getElementsByTagName('thead')[0].offsetHeight;
            this.rootDOMNode.setAttribute('style', 'margin-top: ' + tableHeaderHeight + 'px');
        }

    }

    /**
     *  Removes any event handlers added.
     */
    componentWillUnmount() {

        this.tableIsMounted = false;

        if (this.scrolledElement) {
            this.scrolledElement.removeEventListener('scroll', this.scrollHandler);
            this.scrollEventAdded = false;
        }

    }

    /**
     * Checks whether the "rowsModel" property has the needed format and it's properties are of required types.
     *
     * @param rowsModel
     */
    checkRowsModel(rowsModel) {

        if (!utils.isArray(rowsModel)) {
            throw new TypeError('The rowsModel is not an array.');
        }

        for (let i = 0; i < rowsModel.length; i++) {

            if (!utils.isObject(rowsModel[i])) {
                throw new TypeError('Item number ' + i + ' from rowsModel is not an object.');
            }

            if (typeof rowsModel[i].fieldId !== 'string') {
                throw new TypeError('Entry number ' + i +
                    ' in the rowsModel array does not contain a proper "fieldId" property');
            }

        }

    }

    /**
     * Inserts rows into the table if any.
     *
     * @param   {string}   url            The URL from which the table data will be fetched.
     * @param   {string}   pageNumber     The page number from where the data fetching will start.
     * @param   {string}   pageSize       The size of the page.
     * @param   {Object}   urlParams      The optional query parameters that will be attached to the table URL.
     * @param   {function} showSpinner    The reference to the show table loading spinner method.
     * @param   {function} hideSpinner    The reference to the show table loading spinner method.
     * @param   {Array}    dataStore      The table entries store in case the user is in "View Selected Items" mode.
     * @param   {boolean}  clearEntries   Flag which tells if table entries should also be cleared.
     * @return  {Promise}                 The promise that gets resolved after the rows have been added to the table.
     */
    addRowsToTable(url = '',
                   pageNumber = '',
                   pageSize = '',
                   urlParams,
                   showSpinner,
                   hideSpinner,
                   dataStore,
                   tableRequestPayload,
                   clearEntries = false) {

        if (this.endReached) { return; }

        if (clearEntries) {
            this.setState({
                emptyCellMessageId: this.props.emptyCellMessageId,
                entries:            [],
                noEntries:          false
            });
        }

        // The inner table needs a small hack in order to avoid overlapping of the spinner over the table body scrollbar
        if (this.props.innerTable && this.rootDOMNode) {
            this.rootDOMNode.classList.add('in-loading-mode');
        }

        showSpinner();

        // Execute the "startAddingEntries" lifecycle event handler if any
        // if (utils.isObject(this.props.lifecycleEvents) &&
        //     typeof this.props.lifecycleEvents.startAddingEntries === 'function') {
        //     this.props.lifecycleEvents.startAddingEntries();
        // }

        this.addingRows = true;
        this.requestParams = api.buildQueryParams(pageNumber, pageSize, urlParams);
        this.requestData = tableRequestPayload;
        this.url = url;

        return api.getTableData(
            url,
            pageNumber,
            pageSize,
            urlParams,
            dataStore,
            this.verb,
            tableRequestPayload
        ).then(
            (response) => {

                // Prevent the situation when the response comes slower but the user has navigated away from the page.
                if (!this.tableIsMounted) {
                    return;
                }

                // Does a more loose comparison than _.isEqual() because it can compare strings with other types
                let compareFunction = (a, b) => {
                    for (let key in a) {
                        if (a.hasOwnProperty(key) && a[key] != b[key])
                            return false;
                    }

                    return true;
                };

                if (response.req) {
                    let baseUrl     = response.req.url.substring(0, response.req.url.indexOf('?'));

                    if (baseUrl !== this.url) {
                        console.log("WARN: Ignoring out of order paged request: url mismatch");
                        console.log(baseUrl);
                        console.log(this.url);
                        return;
                    }

                    if (response.req.method !== this.verb) {
                        console.log("WARN: Ignoring out of order paged request: verb mismatch");
                        console.log(response.req.method);
                        console.log(this.verb);
                        return;
                    }

                    let queryParams = api.splitQueryParams(response.req.url);

                    if (!isEqualWith(this.requestParams, queryParams, compareFunction)) {
                        console.log("WARN: Ignoring out of order paged request: query param mismatch");
                        console.log(this.requestParams);
                        console.log(queryParams);
                        return;
                    }

                    if (this.verb === POST) {
                        // compare the query params for POST
                        if (!isEqualWith(response.req._data, this.requestData, compareFunction)) {
                            console.log("WARN: Ignoring out of order paged request: body mismatch");
                            console.log(response.req._data);
                            console.log(this.requestData);
                            return;
                        }
                    }

                }

                var newEntries;

                if (utils.isArray(response.body)) {
                    newEntries = response.body;
                } else if (utils.isArray(response.body[this.props.nestedObject])) {
                    newEntries = response.body[this.props.nestedObject];
                } else if (response.body[this.props.nestedObject] == null) {
                    newEntries = [];
                } else {
                    throw new TypeError('The response is expected to be an array. ' +
                        Object.prototype.toString.call(newEntries) + ' given instead.');
                }

                if (!newEntries || newEntries.length < parseInt(pageSize, 10)) {
                    this.endReached = true;
                }

                // Execute the "justBeforeAddingEntries" lifecycle event handler if any
                if (utils.isObject(this.props.lifecycleEvents) &&
                    typeof this.props.lifecycleEvents.justBeforeAddingEntries === 'function') {
                    this.props.lifecycleEvents.justBeforeAddingEntries(response, this);
                }

                this.setState(
                    Object.assign(
                        {},
                        this.state,
                        {entries: [...this.state.entries, ...newEntries]}
                    )
                );

                if (this.endReached && this.state.entries.length === 0) {
                    this.setState(
                        Object.assign(
                            {},
                            this.state,
                            {noEntries: true}
                        )
                    );
                }

                // Execute the "finishedAddingEntries" lifecycle event handler if any
                if (utils.isObject(this.props.lifecycleEvents) &&
                    typeof this.props.lifecycleEvents.finishedAddingEntries === 'function') {
                    this.props.lifecycleEvents.finishedAddingEntries(response, this);
                }

                this.addingRows = false;

                hideSpinner();

                if (this.props.innerTable && this.rootDOMNode) {
                    this.rootDOMNode.classList.remove('in-loading-mode');
                }

                if (dataStore) {
                    this.endReached = true;
                    if (this.props.datastoreSpinner === true) this.showSpinner();
                    if (this.props.datastoreSpinner === false) this.hideSpinner();
                }

            }
        ).catch((error) => {
            utils.logError(error, 'CustomTableBody.index.addRowsToTable():');
            xhr.handleAllErrors(error);
        });

    }

    /**
     * Returns the number of items loaded in the table.
     *
     * @return  {number}   The total number of entries loaded in the table.
     */
    getNumberOfEntries() {
        return this.state.entries.length;
    }

    /**
     * Returns a new array with all the table entries.
     *
     * @return  {Array}   The copy of the array with the table entries.
     */
    getEntries() {
        return this.tableIsMounted ? [...this.state.entries] : null;
    }

    /**
     * Sets the entries of the table.
     *
     * @param  {Array} newEntries   The array of new entries to be set.
     */
    setEntries(newEntries) {

        if (this.tableIsMounted) { // Check if the table still exists (a component might
            // route to another page when this check is executed).

            this.pageNumber = 0;
            this.setState(
                Object.assign(
                    {},
                    this.state,
                    {entries: [...newEntries]}
                )
            );

            // Check if there are still any table entries left
            if (this.state.entries.length !== 0) {
                this.setState(
                    Object.assign(
                        {},
                        this.state,
                        {noEntries: false}
                    )
                );
            } else {
                this.setState(
                    Object.assign(
                        {},
                        this.state,
                        {noEntries: true}
                    )
                );
            }

        }

    }

    /**
     *  Toggles the state of the header from static positioning to fixed positioning and vice-versa.
     */
    toggleFixedHeader() {

        let tableWrapper       = document.getElementById(this.props.tableId),
            tableWrapperCoords = tableWrapper.getBoundingClientRect(),
            tableHeader        = tableWrapper.getElementsByTagName('thead')[0],
            tableHeaderHeight  = tableHeader.offsetHeight;

        if (tableWrapperCoords.top <= 0 && !this.isHeaderFixed) {

            tableHeader.className = tableHeader.className + ' fixed';
            tableWrapper.setAttribute('style', 'padding-top: ' + tableHeaderHeight + 'px;');
            this.isHeaderFixed = true;

        } else if (tableWrapperCoords.top > 0 && this.isHeaderFixed) {

            tableHeader.className = tableHeader.className.replace(' fixed', '');
            tableWrapper.setAttribute('style', '');
            this.isHeaderFixed = false;

        }

    }

    /**
     * Infinite scroll functionality.
     */
    addInfiniteScroll() {

        let tableBody = this.rootDOMNode;
        this.scrolledElement = this.props.innerTable ? tableBody : window;

        this.scrollHandler = (event) => {

            let bottomScrollOffset = 600,
                shouldAddNewRows   = false;

            if (this.props.innerTable) {
                let bottomDistance = tableBody.scrollHeight - tableBody.scrollTop - tableBody.clientHeight;
                shouldAddNewRows = bottomDistance < bottomScrollOffset;
            } else {
                this.toggleFixedHeader();

                let pageY = (window.scrollY || window.pageYOffset);
                shouldAddNewRows = (document.body.offsetHeight - bottomScrollOffset) < (window.innerHeight + pageY);
            }

            if (shouldAddNewRows && !this.addingRows) {

                this.pageNumber++;
                this.addingRows = true;

                this.addRowsToTable(
                    this.url,
                    this.pageNumber,
                    this.pageSize,
                    this.props.urlParams,
                    this.showSpinner,
                    this.hideSpinner,
                    this.dataStore,
                    this.props.tableRequestPayload
                );

            }

        };

        this.scrolledElement.addEventListener('scroll', this.scrollHandler);

    }

    /**
     * Renders the CustomTableBody component.
     *
     * @return  {HTML}   The DOM elements of the component.
     */
    render() {

        return this.state.entries.length !== 0 ?
            <Rows table={this}
                  appendStartIndex={this.pageNumber * this.pageSize}
                  rowsModel={this.props.rowsModel}
                  columnWidths={this.props.columnWidths} /> :
            <NoEntriesRow colspan={this.props.rowsModel.length} noEntries={this.state.noEntries} />;
    }

    getPageNumber() {
        return this.pageNumber;
    }

}

export default CustomTableBody;



// WEBPACK FOOTER //
// ./src/scripts/reusable/CustomTableBody/CustomTableBody.index.jsx